// ================================================================================================
// Web Thing
// ================================================================================================
import _ from "lodash";

import { isDefined, getUrlParameters, getRandomSmallKey } from "@Utils/Micro";

import { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { Localization } from '@Primitive/Localization/Localization';

import { Plegma } from './Api';
import { DebugEx } from '@Utils/DebugEx';
import { StateStatus, WebThingKeys, WebThingPortType } from './WebNodeTypes';
import { WebNode } from './WebNode';

import type { WebModule } from "./WebModule";

// ================================================================================================

export class WebThingPort {
    // --------------------------------------------------------------------------------------------
    public PlegmaPort: Plegma.Port;
    public PortType: WebThingPortType;
    public WebThing: WebThing;
    public LastStateStatus: StateStatus;
    // --------------------------------------------------------------------------------------------
    // Events
    public onPortEvent: (event: Plegma.PortEvent, port: WebThingPort) => void;
    // --------------------------------------------------------------------------------------------
    // CB
    public cbPortDataValidation: (data: any) => IWidgetDataValidateResp;
    // --------------------------------------------------------------------------------------------
    // Properties
    get PortKey(): string { return this.PlegmaPort.PortKey; }
    get State(): any { return this.PlegmaPort.State; }
    set State(state: any) { this.PlegmaPort.State = state }
    // --------------------------------------------------------------------------------------------
    constructor(
        port: Plegma.Port,
        portType: WebThingPortType,
        cbPortDataValidation?: (data: any) => IWidgetDataValidateResp,
        onPortEvent?: (event: Plegma.PortEvent, port: WebThingPort) => void) {

        if (!isDefined(port.PortKey)) {
            DebugEx.TraceCritical("Don't use null/undefined port key!!!!", port);
        }
        else if (port.PortKey.indexOf("-") != -1) {
            DebugEx.TraceCritical("Don't use - in port key!!!!", port);
        }

        this.PlegmaPort = port;
        this.PortType = portType
        this.onPortEvent = onPortEvent;
        this.cbPortDataValidation = cbPortDataValidation;
        this.LastStateStatus = StateStatus.NoData;
    }
    // --------------------------------------------------------------------------------------------
}

// ================================================================================================

export class WebThing {
    // --------------------------------------------------------------------------------------------
    public Name: string;
    // --------------------------------------------------------------------------------------------
    public PlegmaThing: Plegma.Thing;
    public Ports: { [key: string]: WebThingPort };
    // --------------------------------------------------------------------------------------------
    private PortsIncCount: number;
    // --------------------------------------------------------------------------------------------
    // Events
    public onDataTransform: (portKey: string, data: any, outDataCB: (portKey: string, data: any) => void) => boolean = (p, d, c) => false;
    // --------------------------------------------------------------------------------------------
    // Properties
    get ThingKey(): string { return this.PlegmaThing.ThingKey }
    // --------------------------------------------------------------------------------------------
    private ModuleKey: string;
    private NodeKey: string;
    get webModule(): WebModule { return WebNode.GetInstanceKey(this.NodeKey).GetModule(this.ModuleKey) }

    // --------------------------------------------------------------------------------------------
    constructor(module: WebModule, thing: Plegma.Thing, ports?: WebThingPort[]) {
        this.NodeKey = module.NodeKey;
        this.ModuleKey = module.ModuleKey;
        this.PortsIncCount = 100; // Big starting number for correct starting of ports

        // Update thing key with module name
        this.Name = thing.Name;
        thing.Name = this.Name + "(" + module.Name + ")"
        thing.ThingKey = module.ModuleKey + "_" + thing.ThingKey;

        this.PlegmaThing = thing;
        this.PlegmaThing.Ports = [];
        this.PlegmaThing.Hierarchy = "";

        // Pass all ports
        this.Ports = {};
        _.forEach(ports, p => {
            this.Ports[p.PortKey] = p;

            // link the port to this thing
            p.WebThing = this;

            // Link the plegma ports to plegma thing
            this.PlegmaThing.Ports.push(p.PlegmaPort);
        });

        // Be sure that we have the user info port
        this.CreateUserInfoPort();

        // Connect the thing to module
        this.webModule.AddThing(this);
    }
    // --------------------------------------------------------------------------------------------
    public CreateUserInfoPort() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);

        if (!_.has(this.Ports, WebThingKeys.UserEmailKey)) {
            // Add port for user things
            let newPort = new WebThingPort({
                Name: "User Email",
                PortKey: WebThingKeys.UserEmailKey,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                Description: "",
                State: webNode.YodiWS.UserInfo.Email
            }, WebThingPortType.Data);
            newPort.WebThing = this;
            this.Ports[newPort.PortKey] = newPort;
            this.PlegmaThing.Ports.push(newPort.PlegmaPort);
        }

        if (!_.has(this.Ports, WebThingKeys.URLParamsKey)) {
            // Add port for user things
            let newPort = new WebThingPort({
                Name: "URL Params",
                PortKey: WebThingKeys.URLParamsKey,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                Description: "",
                State: JSON.stringify(getUrlParameters())
            }, WebThingPortType.Data);
            newPort.WebThing = this;
            this.Ports[newPort.PortKey] = newPort;
            this.PlegmaThing.Ports.push(newPort.PlegmaPort);
        }

        if (!_.has(this.Ports, WebThingKeys.BoardLanguageKey)) {
            let localization = Localization.GetInstance(this.NodeKey);

            // Add port for user things
            let newPort = new WebThingPort({
                Name: "Language Code",
                PortKey: WebThingKeys.BoardLanguageKey,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                Description: "The runtime language code",
                State: (localization != null) ? localization.SelectedLanguage : ""
            }, WebThingPortType.Data);
            newPort.WebThing = this;
            this.Ports[newPort.PortKey] = newPort;
            this.PlegmaThing.Ports.push(newPort.PlegmaPort);
        } else {
            let localization = Localization.GetInstance(this.NodeKey);
            this.Ports[WebThingKeys.BoardLanguageKey].State = (localization != null) ? localization.SelectedLanguage : "";
        }

        if (!_.has(this.Ports, WebThingKeys.Location)) {
            // Add port for user things
            let newPort = new WebThingPort({
                Name: "Location",
                PortKey: WebThingKeys.Location,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                Description: "",
                State: "Location" // TODO: read the initial state
            }, WebThingPortType.Data);
            newPort.WebThing = this;
            this.Ports[newPort.PortKey] = newPort;
            this.PlegmaThing.Ports.push(newPort.PlegmaPort);
        }

        if (!_.has(this.Ports, WebThingKeys.Cookie)) {
            // Get cookie from local storage
            let cookie = localStorage.getItem(this.NodeKey + "__COOKIE_KEY__");

            // Add port for user things
            let newPort = new WebThingPort({
                Name: "Cookie",
                PortKey: WebThingKeys.Cookie,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                Description: "",
                State: isDefined(cookie) ? cookie : null
            }, WebThingPortType.Data);
            newPort.WebThing = this;
            this.Ports[newPort.PortKey] = newPort;
            this.PlegmaThing.Ports.push(newPort.PlegmaPort);
        }

        if (!_.has(this.Ports, WebThingKeys.WidgetSettings)) {
            // Add port for user things
            let newPort = new WebThingPort({
                Name: "Widget Settings",
                PortKey: WebThingKeys.WidgetSettings,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                Description: "",
                State: ""
            }, WebThingPortType.Data);
            newPort.WebThing = this;
            this.Ports[newPort.PortKey] = newPort;
            this.PlegmaThing.Ports.push(newPort.PlegmaPort);
        }

        if (!_.has(this.Ports, WebThingKeys.ToolbarIconClick)) {
            // Add port for user things
            let newPort = new WebThingPort({
                Name: "Widget Toolbar Icon Clicked",
                PortKey: WebThingKeys.ToolbarIconClick,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            }, WebThingPortType.Trigger);
            newPort.WebThing = this;
            this.Ports[newPort.PortKey] = newPort;
            this.PlegmaThing.Ports.push(newPort.PlegmaPort);
        }
    }
    // --------------------------------------------------------------------------------------------
    public SendPortEvent(portKey: string, state: any, origState: any) {
        if (_.has(this.Ports, portKey)) {
            let port = this.Ports[portKey];

            // Save state
            port.State = state;

            // Send ports
            this.webModule.SendPortEvent(this, port, state, origState);
        }
    }
    // --------------------------------------------------------------------------------------------
    public AddPort(port: WebThingPort, updateServer: boolean = false) {
        // link the port to this thing
        port.WebThing = this;

        this.Ports[port.PortKey] = port;
        this.PlegmaThing.Ports = _.map(this.Ports, t => t.PlegmaPort);

        // Update the module with the new thing ports
        if (updateServer)
            this.webModule.UpdateThing(this);
    }
    // --------------------------------------------------------------------------------------------
    public RemovePort(port: WebThingPort) {
        // un-link the port to this thing
        port.WebThing = null;

        if (_.has(this.Ports, port.PortKey)) {
            delete this.Ports[port.PortKey];
            this.PlegmaThing.Ports = _.map(this.Ports, t => t.PlegmaPort);
        }

        // Update the module with the new thing ports
        this.webModule.UpdateThing(this);
    }
    // --------------------------------------------------------------------------------------------
    public GetPort(portKey: string): WebThingPort | null {
        if (_.has(this.Ports, portKey)) {
            return this.Ports[portKey];
        }
        return null;
    }
    // --------------------------------------------------------------------------------------------
    public GetNextGeneratedPortKey(): string {
        this.PortsIncCount++;
        return this.PortsIncCount + "_" + getRandomSmallKey();
    }
    // --------------------------------------------------------------------------------------------
    public Rename(name: string) {
        this.Name = name;
        this.PlegmaThing.Name = this.Name + "(" + this.webModule.Name + ")"
        // NOTE: Same code and in WebModule._updateWebNodeThings ...

        // Update the module with the new naming
        this.webModule.UpdateThing(this);
    }
    // --------------------------------------------------------------------------------------------
}

// ================================================================================================